import { EntityFoundationView } from './EntityFoundationView';
import { DmExceptionsView } from './DmExceptionsView';
import {
  getMemberSelfBalance,
  logout,
  URBANIK_RELEASE,
  memberSelfPaymentReceiptDownloadUrl,
  type HealthResponse,
  type MemberSelfBalance,
} from '@urbanik/api-client';

import {
  AppShell,
  CanonicalGallery,
  DataTableGallery,
  FormGallery,
  Icon,
  StateGallery,
  StatePanel,
  canonicalStates,
  formatDateTime,
  formatUsd,
  type AppBreadcrumb,
  type CanonicalStateKind,
  type FormEvidenceMode,
  type TableEvidenceScenario,
  type CanonicalGallerySection,
  type AppNavigationItem,
} from '@urbanik/ui';
import {
  Calculator,
  FileUp,
  GitCompareArrows,
  ReceiptText,
  LayoutDashboard,
  KeyRound,
  Landmark,
  Building2,
  Scale,
  ScrollText,
  Sheet,
  ShieldCheck,
  Tags,
  UserRoundCheck,
  UserSearch,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiBase } from './api-base';
import { openProtectedDocument } from './protected-document';
import { RolesView } from './RolesView';
import { LoginView } from './LoginView';
import { PermissionMatrixView } from './PermissionMatrixView';
import { AccessBoundary, useAccess } from './AccessBoundary';
import { AccessComparisonView } from './AccessComparisonView';
import { MemberExplorerView } from './MemberExplorerView';
import { MembersListView } from './MembersListView';
import { MemberImportReconciliationView } from './MemberImportReconciliationView';
import { CreateMemberView } from './CreateMemberView';
import { MemberDetailView } from './MemberDetailView';
import { EditMemberView } from './EditMemberView';
import { MemberImportWorkspaceView } from './MemberImportWorkspaceView';
import { ProfessionalActsView } from './ProfessionalActsView';
import { ConceptsView } from './ConceptsView';
import { RulesView } from './RulesView';
import { RuleSimulatorView } from './RuleSimulatorView';
import { AdminUsersView } from './AdminUsersView';
import { ObligationModelView } from './ObligationModelView';
import { DmStagingExplorerView } from './DmStagingExplorerView';
import { MemberSelfBalanceView } from './MemberSelfBalanceView';
import { AdminPaymentsView } from './AdminPaymentsView';
import { MemberPaymentRegistrationView } from './MemberPaymentRegistrationView';
import { PaymentDestinationAccountsView } from './PaymentDestinationAccountsView';
import { useAdminPaymentNotifications } from './AdminPaymentNotifications';
import { AdminDashboard } from './AdminDashboard';
import { OrganizationsAdminView } from './OrganizationsAdminView';
import { OrganizationBoundary, useOrganization } from './OrganizationContext';
import { VersionUpdatesView } from './VersionUpdatesView';

export type ShellHealthContract = Pick<HealthResponse, 'timezone'>;
type RouteId =
  | 'mi-saldo'
  | 'registrar-pago'
  | 'fundacion-registro'
  | 'fundacion-conceptos'
  | 'fundacion-entidades'
  | 'entidades'
  | 'dashboard'
  | 'fundamentos'
  | 'estados'
  | 'formularios'
  | 'tablas'
  | 'usuarios'
  | 'roles'
  | 'permisos'
  | 'accesos'
  | 'socios'
  | 'socio-nuevo'
  | 'socio-detalle'
  | 'socio-editar'
  | 'socio-tecnico'
  | 'migracion-socios'
  | 'conciliacion-socios'
  | 'actas'
  | 'conceptos'
  | 'reglas'
  | 'simulador-reglas'
  | 'modelo-obligaciones'
  | 'excepciones-dm'
  | 'explorador-dm'
  | 'pagos'
  | 'cuentas-receptoras'
  | 'version'
  | 'login';
const navigation: readonly AppNavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '#dashboard',
    icon: LayoutDashboard,
    group: 'General',
  },
  { id: 'entidades', label: 'Entidades', href: '#entidades', icon: Building2, group: 'General' },
  { id: 'socios', label: 'Socios', href: '#socios', icon: Users, group: 'Socios' },

  { id: 'pagos', label: 'Pagos', href: '#pagos', icon: ReceiptText, group: 'Finanzas' },
  {
    id: 'cuentas-receptoras',
    label: 'Cuentas receptoras',
    href: '#cuentas-receptoras',
    icon: Landmark,
    group: 'Finanzas',
  },
  {
    id: 'modelo-obligaciones',
    label: 'Obligaciones',
    href: '#modelo-obligaciones',
    icon: Calculator,
    group: 'Finanzas',
  },
  { id: 'actas', label: 'Actas', href: '#actas', icon: ScrollText, group: 'Gobernanza' },
  { id: 'conceptos', label: 'Conceptos', href: '#conceptos', icon: Tags, group: 'Gobernanza' },
  { id: 'reglas', label: 'Reglas', href: '#reglas', icon: Scale, group: 'Gobernanza' },
  {
    id: 'simulador-reglas',
    label: 'Simulador',
    href: '#simulador-reglas',
    icon: Calculator,
    group: 'Gobernanza',
  },
  {
    id: 'migracion-socios',
    label: 'Importar socios',
    href: '#migracion-socios',
    icon: GitCompareArrows,
    group: 'Migración',
  },
  {
    id: 'conciliacion-socios',
    label: 'Conciliar socios',
    href: '#conciliacion-socios',
    icon: FileUp,
    group: 'Migración',
  },
  {
    id: 'excepciones-dm',
    label: 'Excepciones DM',
    href: '#excepciones-dm',
    icon: Sheet,
    group: 'Migración',
  },
  {
    id: 'explorador-dm',
    label: 'Explorador DM',
    href: '#explorador-dm',
    icon: Sheet,
    group: 'Migración',
  },
  { id: 'usuarios', label: 'Usuarios', href: '#usuarios', icon: Users, group: 'Seguridad' },
  { id: 'roles', label: 'Roles', href: '#roles', icon: ShieldCheck, group: 'Seguridad' },
  { id: 'permisos', label: 'Permisos', href: '#permisos', icon: KeyRound, group: 'Seguridad' },
  { id: 'accesos', label: 'Mi acceso', href: '#accesos', icon: UserRoundCheck, group: 'Seguridad' },
];
export function routeFromHash(): RouteId {
  if (window.location.hash.startsWith('#version')) return 'version';
  if (window.location.hash.startsWith('#entidades')) return 'entidades';
  if (window.location.hash.startsWith('#fundacion-registro')) return 'fundacion-registro';
  if (window.location.hash.startsWith('#fundacion-conceptos')) return 'fundacion-conceptos';
  if (window.location.hash.startsWith('#fundacion-entidades')) return 'fundacion-entidades';
  if (window.location.hash.startsWith('#mi-saldo')) return 'mi-saldo';
  if (window.location.hash === '#registrar-pago') return 'registrar-pago';
  if (window.location.hash === '#dashboard' || window.location.hash === '#inicio')
    return 'dashboard';
  if (window.location.hash === '#fundamentos') return 'fundamentos';
  if (window.location.hash.startsWith('#estados')) return 'estados';
  if (window.location.hash.startsWith('#formularios')) return 'formularios';
  if (window.location.hash.startsWith('#tablas')) return 'tablas';
  if (window.location.hash.startsWith('#galeria')) return 'dashboard';
  if (window.location.hash.startsWith('#usuarios')) return 'usuarios';
  if (window.location.hash.startsWith('#roles')) return 'roles';
  if (window.location.hash.startsWith('#permisos')) return 'permisos';
  if (window.location.hash.startsWith('#accesos')) return 'accesos';
  if (window.location.hash.startsWith('#socios/nuevo')) return 'socio-nuevo';
  if (/^#socios\/[0-9a-f-]+\/editar$/i.test(window.location.hash)) return 'socio-editar';
  if (/^#socios\/[0-9a-f-]+(?:\?.*)?$/i.test(window.location.hash)) return 'socio-detalle';
  if (window.location.hash.startsWith('#socios')) return 'socios';
  if (window.location.hash.startsWith('#socio-tecnico')) return 'socio-tecnico';
  if (window.location.hash.startsWith('#actas')) return 'actas';
  if (window.location.hash.startsWith('#conceptos')) return 'conceptos';
  if (window.location.hash.startsWith('#simulador-reglas')) return 'simulador-reglas';
  if (window.location.hash.startsWith('#modelo-obligaciones')) return 'modelo-obligaciones';
  if (window.location.hash.startsWith('#excepciones-dm')) return 'excepciones-dm';
  if (window.location.hash.startsWith('#explorador-dm')) return 'explorador-dm';
  if (window.location.hash.startsWith('#pagos')) return 'pagos';
  if (window.location.hash.startsWith('#cuentas-receptoras')) return 'cuentas-receptoras';
  if (window.location.hash.startsWith('#reglas')) return 'reglas';
  if (window.location.hash.startsWith('#migracion-socios')) return 'migracion-socios';
  if (window.location.hash.startsWith('#login')) return 'login';
  if (window.location.hash.startsWith('#conciliacion-socios')) return 'conciliacion-socios';
  if (window.location.hash.startsWith('#session')) return 'login';
  return 'login';
}
function useRoute(): RouteId {
  const [route, setRoute] = useState<RouteId>(routeFromHash);
  useEffect(() => {
    const update = () => setRoute(routeFromHash());
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  return route;
}
function currentSurface(): 'Web' | 'PWA' | 'Electron' {
  if (window.urbanikPlatform?.runtime === 'electron') return 'Electron';
  if (window.matchMedia?.('(display-mode: standalone)').matches) return 'PWA';
  return 'Web';
}

function ShellOverview() {
  return (
    <div className="shell-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M01.2 · Shell de aplicación</p>
          <h1>Un marco claro para cada tarea.</h1>
          <p>
            La navegación, el contexto y el contenido comparten una estructura estable en Web, PWA y
            Electron.
          </p>
        </div>
        <span className="surface-badge">
          <Icon name="layout" /> {currentSurface()}
        </span>
      </header>
      <section className="shell-showcase" aria-labelledby="anatomy-title">
        <div className="section-title">
          <p className="kicker">Anatomía canónica</p>
          <h2 id="anatomy-title">Todo en su lugar</h2>
        </div>
        <div className="anatomy-grid">
          <article>
            <span>01</span>
            <Icon name="menu" />
            <h3>Navegación lateral</h3>
            <p>Se expande, se contrae y señala la ruta activa sin perder contexto.</p>
          </article>
          <article>
            <span>02</span>
            <Icon name="layout" />
            <h3>Encabezado contextual</h3>
            <p>Reúne control de navegación, breadcrumbs y usuario.</p>
          </article>
          <article>
            <span>03</span>
            <Icon name="home" />
            <h3>Área de contenido</h3>
            <p>Mantiene ancho, ritmo y lectura consistentes en cada superficie.</p>
          </article>
        </div>
      </section>
      <section className="interaction-note" aria-labelledby="interaction-title">
        <Icon name="info" />
        <div>
          <p className="kicker">Prueba interactiva</p>
          <h2 id="interaction-title">Comprueba el shell</h2>
          <p>
            Contrae la barra lateral, cambia a Fundamentos, abre el contexto de usuario y reduce el
            viewport. Todos los controles realizan una acción verificable.
          </p>
        </div>
      </section>
    </div>
  );
}
function FoundationsView() {
  const colors = [
    ['Azul principal', '#092B53'],
    ['Azul profundo', '#0F2B4E'],
    ['Turquesa', '#0EA5B4'],
    ['Verde lima', '#87AF0B'],
  ] as const;
  return (
    <div className="shell-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M01.1 · Fundamentos aprobados</p>
          <h1>Identidad y formatos compartidos.</h1>
          <p>La base visual permanece disponible dentro del nuevo shell canónico.</p>
        </div>
        <span className="surface-badge">
          <Icon name="palette" /> Canónico
        </span>
      </header>
      <section className="foundation-summary" aria-label="Resumen de fundamentos">
        <div className="brand-swatches">
          {colors.map(([name, value]) => (
            <article style={{ backgroundColor: value }} key={name}>
              <strong>{name}</strong>
              <code>{value}</code>
            </article>
          ))}
        </div>
        <div className="format-summary">
          <article>
            <span>Moneda USD</span>
            <strong>{formatUsd('1234.50')}</strong>
            <small>Valor técnico de ejemplo</small>
          </article>
          <article>
            <span>Fecha y hora local</span>
            <strong>{formatDateTime('2026-08-21T02:30:00.000Z')}</strong>
            <small>America/Guayaquil</small>
          </article>
        </div>
      </section>
    </div>
  );
}
function stateFromHash(): CanonicalStateKind {
  const selected = new URLSearchParams(window.location.hash.split('?')[1]).get('state');
  return ['loading', 'empty', 'error', 'forbidden', 'offline', 'success'].includes(selected ?? '')
    ? (selected as CanonicalStateKind)
    : 'loading';
}
function StatesView() {
  return (
    <div className="shell-page states-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M01.3 · Estados canónicos</p>
          <h1>Una respuesta clara en cada situación.</h1>
          <p>
            Usa el conmutador para inspeccionar carga, vacío, error, sin permiso, offline y éxito
            con el mismo lenguaje visual en todas las superficies.
          </p>
        </div>
        <span className="surface-badge">
          <Icon name="info" /> Interactivo
        </span>
      </header>
      <StateGallery initialState={stateFromHash()} />
    </div>
  );
}
function formEvidenceFromHash(): FormEvidenceMode {
  const selected = new URLSearchParams(window.location.hash.split('?')[1]).get('view');
  return ['errors', 'dialog', 'success'].includes(selected ?? '')
    ? (selected as FormEvidenceMode)
    : 'default';
}
function FormsView() {
  return (
    <div className="shell-page forms-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M01.4 · Formularios canónicos</p>
          <h1>Capturar, revisar y confirmar con confianza.</h1>
          <p>
            Campos, ayudas, errores y confirmación comparten un patrón accesible que conserva cada
            dato durante todo el recorrido.
          </p>
        </div>
        <span className="surface-badge">
          <Icon name="layout" /> Interactivo
        </span>
      </header>
      <FormGallery evidenceMode={formEvidenceFromHash()} />
    </div>
  );
}

function tableScenarioFromHash(): TableEvidenceScenario {
  const selected = new URLSearchParams(window.location.hash.split('?')[1]).get('scenario');
  return selected === 'zero' || selected === 'few' ? selected : 'many';
}
function TablesView() {
  return (
    <div className="shell-page tables-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M01.5 · Tablas y filtros</p>
          <h1>Encontrar lo importante, sin perder contexto.</h1>
          <p>
            Una tabla técnica compartida demuestra búsqueda, filtros, orden, paginación, acciones y
            control de columnas en todas las superficies.
          </p>
        </div>
        <span className="surface-badge">
          <Icon name="spacing" /> Interactivo
        </span>
      </header>
      <DataTableGallery initialScenario={tableScenarioFromHash()} />
    </div>
  );
}

function gallerySectionFromHash(): CanonicalGallerySection {
  const selected = new URLSearchParams(window.location.hash.split('?')[1]).get('section');
  return ['fundamentos', 'shell', 'estados', 'formularios', 'tablas'].includes(selected ?? '')
    ? (selected as CanonicalGallerySection)
    : 'fundamentos';
}
function GalleryView() {
  return (
    <div className="shell-page gallery-page">
      <header className="page-heading">
        <div>
          <p className="kicker">M01.6 · Galería canónica</p>
          <h1>Todo el sistema visual, en un solo lugar.</h1>
          <p>
            El catálogo reúne los componentes y variantes aprobadas y fija su contrato de
            reutilización para las fases siguientes.
          </p>
        </div>
        <span className="surface-badge">
          <Icon name="palette" /> Catálogo único
        </span>
      </header>
      <CanonicalGallery initialSection={gallerySectionFromHash()} />
    </div>
  );
}

const routeLabels: Readonly<Record<Exclude<RouteId, 'login'>, string>> = {
  entidades: 'Entidades',
  'fundacion-registro': 'Actas y reglas por entidad',
  'fundacion-conceptos': 'Conceptos por entidad',
  'fundacion-entidades': 'Fundación multi-entidad',
  dashboard: 'Dashboard',
  fundamentos: 'Fundamentos',
  estados: 'Estados',
  formularios: 'Formularios',
  tablas: 'Tablas',
  'mi-saldo': 'Mi saldo',
  'registrar-pago': 'Registrar pago',
  usuarios: 'Usuarios',
  roles: 'Roles',
  permisos: 'Permisos',
  accesos: 'Mi acceso',
  socios: 'Socios',
  'socio-nuevo': 'Nuevo socio',
  'socio-detalle': 'Detalle de socio',
  'socio-editar': 'Editar socio',
  'socio-tecnico': 'Socio técnico',
  'migracion-socios': 'Migración de socios',
  'conciliacion-socios': 'Conciliación de socios',
  actas: 'Actas',
  conceptos: 'Conceptos',
  reglas: 'Reglas',
  'simulador-reglas': 'Simulador',
  'modelo-obligaciones': 'Obligaciones',
  'excepciones-dm': 'Excepciones DM',
  'explorador-dm': 'Explorador DM',
  pagos: 'Pagos',
  'cuentas-receptoras': 'Cuentas receptoras',
  version: 'Versión y novedades',
};
function breadcrumbsForRoute(route: Exclude<RouteId, 'login'>): readonly AppBreadcrumb[] {
  if (route === 'dashboard') return [{ label: 'Dashboard' }];
  const memberChild =
    route === 'socio-nuevo' || route === 'socio-detalle' || route === 'socio-editar';
  return [
    { label: 'Dashboard', href: '#dashboard' },
    ...(memberChild ? [{ label: 'Socios', href: '#socios' }] : []),
    { label: routeLabels[route] },
  ];
}
function navigationIdForRoute(route: Exclude<RouteId, 'login'>): string {
  return route === 'socio-nuevo' || route === 'socio-detalle' || route === 'socio-editar'
    ? 'socios'
    : route;
}

function AuthorizedApp({ route }: { route: Exclude<RouteId, 'login'> }) {
  const access = useAccess();
  const organization = useOrganization();
  const [memberPayment, setMemberPayment] = useState<MemberSelfBalance['latestPayment']>(null);
  const [paymentDownloadError, setPaymentDownloadError] = useState('');
  const [reviewedPaymentId, setReviewedPaymentId] = useState(() =>
    localStorage.getItem('urbanik:reviewed-payment'),
  );
  const hasPermission = (permission: string) => access.permissions.includes(permission);
  const adminPaymentNotifications = useAdminPaymentNotifications(hasPermission('payments.review'));
  const previousMemberPaymentId = useRef<string | null>(null);
  const canReadOwnBalance =
    access.permissions.includes('collections.own_balance.read') &&
    !access.permissions.includes('members.list.read');
  const refreshMemberPayment = useCallback(async () => {
    if (!canReadOwnBalance) return;
    try {
      const value = await getMemberSelfBalance(apiBase);
      const next = value.latestPayment;
      setMemberPayment(next);
      if (
        next?.id &&
        previousMemberPaymentId.current &&
        previousMemberPaymentId.current !== next.id
      )
        window.dispatchEvent(
          new CustomEvent('urbanik:member-payment-updated', { detail: next.id }),
        );
      previousMemberPaymentId.current = next?.id ?? null;
    } catch {
      // A transient refresh failure must not remove a notification already shown.
    }
  }, [canReadOwnBalance]);
  useEffect(() => {
    if (!canReadOwnBalance) return;
    void refreshMemberPayment();
    const timer = window.setInterval(() => void refreshMemberPayment(), 5000);
    const onFocus = () => void refreshMemberPayment();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshMemberPayment();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [canReadOwnBalance, refreshMemberPayment]);
  const unreadPayment = memberPayment?.id === reviewedPaymentId ? null : memberPayment;
  const reviewPayment = () => {
    if (!memberPayment) return;
    localStorage.setItem('urbanik:reviewed-payment', memberPayment.id);
    setReviewedPaymentId(memberPayment.id);
    window.location.hash = `#mi-saldo?payment=${memberPayment.id}`;
    window.dispatchEvent(
      new CustomEvent('urbanik:open-payment-detail', { detail: memberPayment.id }),
    );
  };
  const paymentNotification = unreadPayment ? (
    <article className="ui-notifications__item">
      <strong>Pago aplicado</strong>
      <p>
        Comprobante {unreadPayment.sourceReference}:{' '}
        {unreadPayment.applications.filter((item) => item.status === 'paid').length} cuotas pagadas
        {unreadPayment.applications.some((item) => item.status === 'partially_paid')
          ? ' y un abono parcial'
          : ''}
        . Total {formatUsd(unreadPayment.appliedAmount)}.
      </p>
      {paymentDownloadError ? <p role="alert">{paymentDownloadError}</p> : null}
      <div className="ui-notifications__actions">
        <button type="button" className="ui-button ui-button--secondary" onClick={reviewPayment}>
          Ver detalle
        </button>
        <button
          type="button"
          className="ui-button ui-button--primary"
          onClick={() => {
            setPaymentDownloadError('');
            void openProtectedDocument(
              memberSelfPaymentReceiptDownloadUrl(apiBase, unreadPayment.id),
              'download',
              'recibo.png',
            ).catch((failure) =>
              setPaymentDownloadError(
                failure instanceof Error ? failure.message : 'No se pudo descargar el recibo.',
              ),
            );
          }}
        >
          Descargar recibo oficial
        </button>
      </div>
    </article>
  ) : undefined;
  const visibleNavigation = navigation.filter(
    (item) =>
      (item.id !== 'entidades' || hasPermission('identity.users.read')) &&
      (item.id !== 'usuarios' || hasPermission('identity.users.read')) &&
      (item.id !== 'roles' || hasPermission('identity.roles.read')) &&
      (item.id !== 'permisos' || hasPermission('identity.permissions.read')) &&
      (item.id !== 'socios' || hasPermission('members.list.read')) &&
      (item.id !== 'socio-tecnico' || hasPermission('members.technical-explorer.read')) &&
      (item.id !== 'migracion-socios' || hasPermission('members.import.simulate')) &&
      (item.id !== 'actas' || hasPermission('governance.acts.read')) &&
      (item.id !== 'conceptos' || hasPermission('governance.concepts.read')) &&
      (item.id !== 'reglas' || hasPermission('governance.rules.read')) &&
      (item.id !== 'simulador-reglas' || hasPermission('governance.rule_simulations.run')) &&
      (item.id !== 'modelo-obligaciones' || hasPermission('collections.obligations.read')) &&
      (item.id !== 'excepciones-dm' || hasPermission('financial_migration.dm_exceptions.read')) &&
      (item.id !== 'explorador-dm' || hasPermission('financial_migration.dm_staging.read')) &&
      (item.id !== 'pagos' || hasPermission('payments.create_for_member')) &&
      (item.id !== 'cuentas-receptoras' || hasPermission('payments.destination_accounts.manage')) &&
      (item.id !== 'conciliacion-socios' || hasPermission('members.import.reconcile')),
  );
  const user = {
    name: access.principal.displayName,
    detail: access.roles.join(', ') || 'Acceso restringido',
    initials: access.principal.displayName
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase(),
  };
  if (hasPermission('collections.own_balance.read') && !hasPermission('members.list.read'))
    return (
      <AppShell
        navigation={[
          { id: 'mi-saldo', label: 'Mi saldo', href: '#mi-saldo', icon: Calculator },
          ...(hasPermission('payments.own.create')
            ? [
                {
                  id: 'registrar-pago',
                  label: 'Registrar pago',
                  href: '#registrar-pago',
                  icon: ReceiptText,
                },
              ]
            : []),
        ]}
        activeId={
          route === 'version'
            ? 'version'
            : route === 'registrar-pago'
              ? 'registrar-pago'
              : 'mi-saldo'
        }
        breadcrumbs={[
          {
            label:
              route === 'version'
                ? 'Versión y novedades'
                : route === 'registrar-pago'
                  ? 'Registrar pago'
                  : 'Mi saldo',
          },
        ]}
        notificationCount={unreadPayment ? 1 : 0}
        notifications={paymentNotification}
        user={{ ...user, detail: 'SOCIO' }}
        release={{ version: URBANIK_RELEASE.version, href: '#version' }}
        {...(organization
          ? {
              organization: {
                items: organization.organizations,
                activeId: organization.active.id,
                onChange: organization.select,
              },
            }
          : {})}
        onLogout={() =>
          void logout(apiBase).finally(() => {
            window.location.hash = '#login';
          })
        }
      >
        {route === 'version' ? (
          <VersionUpdatesView />
        ) : route === 'registrar-pago' && hasPermission('payments.own.create') ? (
          <MemberPaymentRegistrationView />
        ) : (
          <MemberSelfBalanceView />
        )}
      </AppShell>
    );
  const breadcrumbs = breadcrumbsForRoute(route);
  return (
    <AppShell
      navigation={visibleNavigation}
      activeId={navigationIdForRoute(route)}
      breadcrumbs={breadcrumbs}
      notificationCount={adminPaymentNotifications.count}
      notifications={adminPaymentNotifications.notifications}
      user={user}
      release={{ version: URBANIK_RELEASE.version, href: '#version' }}
      {...(organization
        ? {
            organization: {
              items: organization.organizations,
              activeId: organization.active.id,
              onChange: organization.select,
            },
          }
        : {})}
      onLogout={() =>
        void logout(apiBase).finally(() => {
          window.location.hash = '#login';
        })
      }
    >
      {route === 'version' ? <VersionUpdatesView /> : null}
      {route === 'dashboard' ? <AdminDashboard /> : null}
      {route === 'entidades' && hasPermission('identity.users.read') ? (
        <OrganizationsAdminView />
      ) : null}
      {route === 'fundamentos' ? <FoundationsView /> : null}
      {route === 'estados' ? <StatesView /> : null}
      {route === 'formularios' ? <FormsView /> : null}
      {route === 'tablas' ? <TablesView /> : null}
      {route === 'usuarios' && hasPermission('identity.users.read') ? (
        <AdminUsersView permissions={access.permissions} />
      ) : null}
      {route === 'roles' && hasPermission('identity.roles.read') ? (
        <RolesView permissions={access.permissions} />
      ) : null}
      {route === 'permisos' && hasPermission('identity.permissions.read') ? (
        <PermissionMatrixView />
      ) : null}
      {route === 'accesos' ? <AccessComparisonView /> : null}
      {route === 'socios' && hasPermission('members.list.read') ? (
        <MembersListView
          canCreate={hasPermission('members.create')}
          canReadDetail={hasPermission('members.detail.read')}
        />
      ) : null}
      {route === 'socio-nuevo' && hasPermission('members.create') ? <CreateMemberView /> : null}
      {route === 'socio-detalle' && hasPermission('members.detail.read') ? (
        <MemberDetailView
          memberId={window.location.hash.slice('#socios/'.length).split('?')[0]!}
          canUpdate={hasPermission('members.profile.update')}
          canChangeStatus={hasPermission('members.status.update')}
          canReadPhoto={hasPermission('members.profile.photo.read')}
          canManagePhoto={hasPermission('members.profile.photo.manage')}
          canReadLocation={hasPermission('members.profile.location.read')}
          canManageLocation={hasPermission('members.profile.location.manage')}
          canReadHousehold={hasPermission('members.profile.household.read')}
          canManageHousehold={hasPermission('members.profile.household.manage')}
          canReadHouseholdPhotos={hasPermission('members.profile.household.photo.read')}
          canManageHouseholdPhotos={hasPermission('members.profile.household.photo.manage')}
          canReadUpdateAuthorizations={hasPermission('members.profile.authorization.read')}
          canReadPortfolio={hasPermission('collections.obligation_portfolio.read')}
          canManageUpdateAuthorizations={hasPermission('members.profile.authorization.manage')}
        />
      ) : null}
      {route === 'socio-tecnico' && hasPermission('members.technical-explorer.read') ? (
        <MemberExplorerView />
      ) : null}
      {route === 'socio-editar' && hasPermission('members.update') ? (
        <EditMemberView
          canChangeStatus={hasPermission('members.status.update')}
          canManageNumber={hasPermission('members.number.manage')}
          memberId={window.location.hash.slice('#socios/'.length, -'/editar'.length)}
          canReadPhoto={hasPermission('members.profile.photo.read')}
          canManagePhoto={hasPermission('members.profile.photo.manage')}
          canReadLocation={hasPermission('members.profile.location.read')}
          canManageLocation={hasPermission('members.profile.location.manage')}
          canReadHousehold={hasPermission('members.profile.household.read')}
          canManageHousehold={hasPermission('members.profile.household.manage')}
          canReadHouseholdPhotos={hasPermission('members.profile.household.photo.read')}
          canManageHouseholdPhotos={hasPermission('members.profile.household.photo.manage')}
          canReadUpdateAuthorizations={hasPermission('members.profile.authorization.read')}
          canManageUpdateAuthorizations={hasPermission('members.profile.authorization.manage')}
        />
      ) : null}
      {route === 'migracion-socios' && hasPermission('members.import.simulate') ? (
        <MemberImportWorkspaceView canExecute={hasPermission('members.import.execute')} />
      ) : null}
      {route === 'conciliacion-socios' && hasPermission('members.import.reconcile') ? (
        <MemberImportReconciliationView />
      ) : null}
      {route === 'conceptos' && hasPermission('governance.concepts.read') ? (
        <ConceptsView
          canCreate={hasPermission('governance.concepts.create')}
          canUpdate={hasPermission('governance.concepts.update')}
          canActivate={hasPermission('governance.concepts.activate')}
        />
      ) : null}
      {route === 'reglas' && hasPermission('governance.rules.read') ? (
        <RulesView
          canCreate={hasPermission('governance.rules.create')}
          canReadVersions={hasPermission('governance.rule_versions.read')}
          canCreateVersions={hasPermission('governance.rule_versions.create')}
          canLifecycle={hasPermission('governance.rules.lifecycle')}
        />
      ) : null}
      {route === 'simulador-reglas' && hasPermission('governance.rule_simulations.run') ? (
        <RuleSimulatorView />
      ) : null}
      {route === 'modelo-obligaciones' && hasPermission('collections.obligations.read') ? (
        <ObligationModelView
          canCreate={hasPermission('collections.obligations.create')}
          canConfirmBatch={hasPermission('collections.obligation_batches.create')}
          canReadPortfolio={hasPermission('collections.obligation_portfolio.read')}
        />
      ) : null}
      {route === 'excepciones-dm' && hasPermission('financial_migration.dm_exceptions.read') ? (
        <DmExceptionsView />
      ) : null}
      {route === 'explorador-dm' && hasPermission('financial_migration.dm_staging.read') ? (
        <DmStagingExplorerView canImport={hasPermission('financial_migration.dm_staging.import')} />
      ) : null}
      {route === 'pagos' && hasPermission('payments.create_for_member') ? (
        <AdminPaymentsView canApprove={hasPermission('payments.review')} />
      ) : null}
      {route === 'cuentas-receptoras' && hasPermission('payments.destination_accounts.manage') ? (
        <PaymentDestinationAccountsView />
      ) : null}
      {route === 'actas' && hasPermission('governance.acts.read') ? (
        <ProfessionalActsView
          canCreate={hasPermission('governance.acts.create')}
          canUpload={hasPermission('governance.acts.document.upload')}
          canAnalyze={hasPermission('governance.acts.ai.extract')}
          canUpdate={hasPermission('governance.acts.update')}
          canDelete={hasPermission('governance.acts.delete')}
        />
      ) : null}
      {(route === 'entidades' && !hasPermission('identity.users.read')) ||
      (route === 'usuarios' && !hasPermission('identity.users.read')) ||
      (route === 'roles' && !hasPermission('identity.roles.read')) ||
      (route === 'permisos' && !hasPermission('identity.permissions.read')) ||
      (route === 'socios' && !hasPermission('members.list.read')) ||
      (route === 'socio-nuevo' && !hasPermission('members.create')) ||
      (route === 'socio-detalle' && !hasPermission('members.detail.read')) ||
      (route === 'socio-editar' && !hasPermission('members.update')) ||
      (route === 'socio-tecnico' && !hasPermission('members.technical-explorer.read')) ||
      (route === 'migracion-socios' && !hasPermission('members.import.simulate')) ||
      (route === 'conciliacion-socios' && !hasPermission('members.import.reconcile')) ||
      (route === 'actas' && !hasPermission('governance.acts.read')) ||
      (route === 'conceptos' && !hasPermission('governance.concepts.read')) ||
      (route === 'reglas' && !hasPermission('governance.rules.read')) ||
      (route === 'simulador-reglas' && !hasPermission('governance.rule_simulations.run')) ||
      (route === 'excepciones-dm' && !hasPermission('financial_migration.dm_exceptions.read')) ||
      (route === 'explorador-dm' && !hasPermission('financial_migration.dm_staging.read')) ||
      (route === 'modelo-obligaciones' && !hasPermission('collections.obligations.read')) ||
      (route === 'pagos' && !hasPermission('payments.create_for_member')) ||
      (route === 'cuentas-receptoras' && !hasPermission('payments.destination_accounts.manage')) ? (
        <StatePanel
          state={canonicalStates.find((state) => state.kind === 'forbidden')!}
          onAction={() => {
            window.location.hash = '#accesos';
          }}
        />
      ) : null}
    </AppShell>
  );
}

export function App() {
  const route = useRoute();
  if (route === 'fundacion-registro') return <EntityFoundationView key="register" register />;
  if (route === 'fundacion-conceptos') return <EntityFoundationView key="concepts" concepts />;
  if (route === 'fundacion-entidades') return <EntityFoundationView />;
  if (route === 'login') return <LoginView />;
  return (
    <AccessBoundary>
      <OrganizationBoundary>
        <AuthorizedApp route={route} />
      </OrganizationBoundary>
    </AccessBoundary>
  );
}
